import React from "react";
import { Box, Typography, Chip, IconButton } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import Section from "../../../layout/Section";
import BaseTable, {
  TableColumn,
  TableData,
} from "../../../tables/common/BaseTable";
import OutputField from "../../../common/display/OutputField";
import TextEditorField from "../../../common/editor/TextEditorField";
import Dropdown from "../../../common/inputs/Dropdown";
import {
  getStatusColor,
  getStatusLabel,
  STATUS_OPTIONS,
} from "../../../../utils/colors/parcelStatusUtils";
import { colors } from "../../../../styles/colors";

interface OwnersSectionProps {
  owners: any[];
  isLoadingOwners: boolean;
  context: "parcel" | "owner" | null;
  selectedOwnerId: string | null;
  onOwnerClick: (ownerId: string | null) => void;
  selectedOwner: any | null;
  ownerNotizen: string;
  setOwnerNotizen: (value: string) => void;
  onStatusChange?: (ownerId: string, status: string) => void;
  canEdit: boolean;
  isEditMode: boolean;
}

const OwnersSection: React.FC<OwnersSectionProps> = ({
  owners,
  isLoadingOwners,
  context,
  selectedOwnerId,
  onOwnerClick,
  selectedOwner,
  ownerNotizen,
  setOwnerNotizen,
  onStatusChange,
  canEdit,
  isEditMode,
}) => {
  // Status-Optionen mit value (für Backend) und label (für Anzeige)
  const statusOptions = STATUS_OPTIONS.map((opt) => ({
    value: opt.value,
    label: opt.label,
  }));
  // Spalten-Definitionen für Eigentümer-Tabelle
  const ownerColumns: TableColumn[] = [
    {
      key: "relevant",
      label: "Relevant",
      align: "center",
      width: "10%",
      sortable: true,
    },
    {
      key: "name",
      label: "Name",
      align: "left",
      width: "45%",
      sortable: true,
    },
    {
      key: "status",
      label: "Status",
      align: "right",
      width: "45%",
      sortable: true,
    },
  ];

  const renderRelevant = (
    _column: TableColumn,
    _row: TableData,
    value: any,
  ) => {
    if (!value) return null;
    return (
      <CheckCircleIcon
        sx={{ fontSize: 18, color: colors.status.success.main }}
      />
    );
  };

  // Render-Funktion für Eigentümer-Status mit MapView-Farben
  const renderOwnerStatus = (
    column: TableColumn,
    row: TableData,
    value: any,
  ) => {
    const status = value || "Kein Kontakt";
    const statusColor = getStatusColor(status);
    return (
      <Chip
        label={getStatusLabel(status)}
        size="small"
        variant="filled"
        sx={{
          backgroundColor: statusColor,
          color: colors.utility.black,
          fontWeight: 500,
        }}
      />
    );
  };

  // Konvertiere Owners zu TableData
  const ownersTableData: TableData[] = owners.map((owner) => ({
    id: owner.id,
    relevant: owner.relevant ?? false,
    name: owner.name || "-",
    status: owner.status || "Kein Kontakt",
  }));

  const handleOwnerRowClick = (row: TableData) => {
    onOwnerClick(row.id as string);
  };

  // Bestimme den anzuzeigenden Owner für Details
  const selectedOwnerForDetail =
    selectedOwner ||
    (selectedOwnerId ? owners.find((o) => o.id === selectedOwnerId) : null);

  const handleStatusChange = (newStatus: string) => {
    if (selectedOwnerForDetail && onStatusChange) {
      onStatusChange(selectedOwnerForDetail.id, newStatus);
    }
  };

  // Detail-Ansicht für Owner
  const renderOwnerDetail = (owner: any) => {
    const canEditStatus = isEditMode && canEdit && onStatusChange;

    return (
      <Box>
        {/* Obere Zeile: Name, Status */}
        <Box
          sx={{
            display: "flex",
            gap: 4,
            mb: 3,
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <OutputField label="Name" value={owner.name || "-"} />
          {canEditStatus ? (
            <Dropdown
              label="Status"
              value={owner.status || "Kein Kontakt"}
              options={statusOptions}
              onChange={handleStatusChange}
              getColor={getStatusColor}
              labelWidth={60}
            />
          ) : (
            <OutputField
              label="Status"
              value={
                <Chip
                  label={getStatusLabel(owner.status)}
                  size="small"
                  variant="filled"
                  sx={{
                    backgroundColor: getStatusColor(owner.status),
                    color: colors.utility.black,
                    fontWeight: 500,
                  }}
                />
              }
            />
          )}
        </Box>

        {/* Notizen-Feld */}
        <Box sx={{ mt: 4 }}>
          <TextEditorField
            value={ownerNotizen}
            onChange={setOwnerNotizen}
            placeholder="Notizen zum Eigentümer (Rich Text-Format unterstützt)"
            label="Notizen"
            readOnly={!isEditMode || !canEdit}
            labelBackgroundColor="white"
            minHeight="60px"
          />
        </Box>

        {/* Back-Button mit Text */}
        <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            onClick={() => onOwnerClick(null)}
            size="small"
            sx={{
              padding: 0.5,
              minWidth: 32,
              height: 32,
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
                color: "text.primary",
              },
            }}
            aria-label="Zurück zur Übersicht"
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: "1rem",
                fontWeight: "bold",
                lineHeight: 1,
              }}
            >
              &lt;
            </Typography>
          </IconButton>
          <Typography
            variant="body2"
            onClick={() => onOwnerClick(null)}
            sx={{
              color: "text.secondary",
              cursor: "pointer",
              "&:hover": {
                color: "text.primary",
              },
            }}
          >
            Zurück
          </Typography>
        </Box>
      </Box>
    );
  };

  // Bestimme Titel basierend auf Kontext
  const getSectionTitle = () => {
    if (context === "parcel") {
      return "Eigentümer des Flurstücks";
    }
    if (context === "owner" || selectedOwnerId) {
      return "Ausgewählter Eigentümer";
    }
    return "Eigentümer";
  };

  return (
    <Section title={getSectionTitle()} noPadding={!selectedOwnerForDetail}>
      {selectedOwnerForDetail ? (
        // Detail-Ansicht für ausgewählten Owner
        renderOwnerDetail(selectedOwnerForDetail)
      ) : (
        // Tabellen-Ansicht für alle Owners (immer klickbar)
        <BaseTable
          columns={ownerColumns}
          data={ownersTableData}
          onRowClick={handleOwnerRowClick}
          selectedRowId={selectedOwnerId || undefined}
          loading={isLoadingOwners}
          emptyMessage="Keine Eigentümer verfügbar"
          renderCell={(column, row, value) => {
            if (column.key === "relevant") {
              return renderRelevant(column, row, value);
            }
            if (column.key === "status") {
              return renderOwnerStatus(column, row, value);
            }
            return value;
          }}
          stickyHeader={true}
          maxHeight={400}
          showBorder={false}
          variant="compact"
          minWidth={400}
          defaultSortColumn="name"
          defaultSortDirection="asc"
          disablePagination={true}
        />
      )}
    </Section>
  );
};

export default OwnersSection;
